import { useState, useEffect, useCallback, useRef } from 'react';
import { usePublicWebSocketContext } from '../contexts/PublicWebSocketContext';

const API_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`;

/**
 * Public WebSocket Hook for Watch View (Read-Only)
 * Does not require authentication
 *
 * Dual delivery: WebSocket (real-time) + HTTP polling (failsafe every 15s)
 * Ensures events are NEVER missed even if WebSocket drops temporarily.
 */
export const usePublicWebSocket = (matchId) => {
  const { socket, connected } = usePublicWebSocketContext();
  const [joined, setJoined] = useState(false);
  const [events, setEvents] = useState([]);
  const [score, setScore] = useState({ home: 0, away: 0 });
  const [viewers, setViewers] = useState(0);
  const [matchState, setMatchState] = useState(null);
  const [match, setMatch] = useState(null);
  const [clock, setClock] = useState({
    minute: 0,
    second: 0,
    half: 1,
    injuryTime: 0,
    running: false
  });
  const [formationsVisible, setFormationsVisible] = useState(false);

  // Track known event IDs for deduplication
  const knownEventIdsRef = useRef(new Set());

  // Fetch match data on mount
  useEffect(() => {
    if (!matchId) return;

    const fetchMatch = async () => {
      try {
        const response = await fetch(`${API_URL}/api/matches/${matchId}/public`);
        if (!response.ok) throw new Error('Failed to fetch match');
        const data = await response.json();
        setMatch(data.data);
        // Initialize score from HTTP data so overlay shows correct score even before WebSocket connects
        if (data.data.home_score !== undefined) {
          setScore({
            home: data.data.home_score || 0,
            away: data.data.away_score || 0
          });
        }
        // Initialize events from HTTP fetch
        if (data.data.events && data.data.events.length > 0) {
          const httpEvents = data.data.events.map(e => ({
            ...e,
            type: e.event_type,
          }));
          // Mark all as known
          httpEvents.forEach(e => knownEventIdsRef.current.add(e.id));
          setEvents(httpEvents);
        }
      } catch (error) {
        console.error('[Public WS Hook] Error fetching match:', error);
      }
    };

    fetchMatch();
  }, [matchId]);

  // HTTP polling failsafe - fetch events every 15 seconds
  useEffect(() => {
    if (!matchId) return;

    const pollEvents = async () => {
      try {
        const response = await fetch(`${API_URL}/api/matches/${matchId}/public`);
        if (!response.ok) return;
        const data = await response.json();
        if (!data.data) return;

        // Update score from HTTP (always authoritative)
        if (data.data.home_score !== undefined) {
          setScore({
            home: data.data.home_score || 0,
            away: data.data.away_score || 0
          });
        }

        // Merge events - add any that we don't have yet
        if (data.data.events && data.data.events.length > 0) {
          const httpEvents = data.data.events
            .filter(e => !e.is_cancelled)
            .map(e => ({
              ...e,
              type: e.event_type,
            }));

          setEvents(prev => {
            const existingIds = new Set(prev.map(e => e.id));
            const newEvents = httpEvents.filter(e => !existingIds.has(e.id));
            if (newEvents.length > 0) {
              console.log('[Public WS Poll] Found', newEvents.length, 'missed events via HTTP');
              newEvents.forEach(e => knownEventIdsRef.current.add(e.id));
              // Merge: put new events at beginning (newest first)
              const merged = [...newEvents, ...prev];
              // Sort newest first by created_at
              merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
              return merged;
            }
            return prev;
          });
        }
      } catch (error) {
        // Silent fail - this is just a failsafe
      }
    };

    const interval = setInterval(pollEvents, 5000);
    return () => clearInterval(interval);
  }, [matchId]);

  // Join match on mount AND on every reconnect
  useEffect(() => {
    if (!socket || !matchId) {
      return;
    }

    const numericMatchId = parseInt(matchId, 10);

    const joinRoom = () => {
      if (socket.connected) {
        console.log('[Public WS] Joining room match_' + numericMatchId);
        socket.emit('match:join', { matchId: numericMatchId });
      }
    };

    // Join immediately if already connected
    joinRoom();

    // Always register connect handler so we re-join after reconnect
    socket.on('connect', joinRoom);

    return () => {
      socket.off('connect', joinRoom);
      if (socket.connected) {
        socket.emit('match:leave', { matchId: numericMatchId });
      }
    };
  }, [socket, matchId]);

  // Stable handlers without stale closures
  const handleJoined = useCallback((data) => {
    console.log('[Public WS] Joined room, state:', data.state ? 'yes' : 'no');
    setJoined(true);
    setViewers(data.viewers || 0);

    if (data.state) {
      setScore(data.state.score || { home: 0, away: 0 });
      setEvents(data.state.events || []);
      setMatchState(data.state);

      // Track event IDs from join state
      if (data.state.events) {
        data.state.events.forEach(e => knownEventIdsRef.current.add(e.id));
      }

      if (data.state.homeTeam && data.state.awayTeam) {
        // Merge with existing match data to preserve formations from HTTP fetch
        setMatch(prev => ({
          ...prev,
          home_team_id: data.state.homeTeam.id,
          away_team_id: data.state.awayTeam.id,
          home_team: data.state.homeTeam,
          away_team: data.state.awayTeam
        }));
      }
    }
  }, []);

  const handleScoreUpdate = useCallback((data) => {
    setScore({
      home: data.homeScore,
      away: data.awayScore
    });
  }, []);

  const handleNewEvent = useCallback((data) => {
    console.log('[Public WS] Received match:event:new', data.event?.id);
    setEvents(prev => {
      // Dedup: skip if we already have this event
      if (prev.some(e => e.id === data.event.id)) {
        console.log('[Public WS] Event', data.event.id, 'already exists, skipping');
        return prev;
      }
      knownEventIdsRef.current.add(data.event.id);
      const newEvents = [data.event, ...prev];
      console.log('[Public WS] Events array updated, length:', newEvents.length);
      return newEvents;
    });
  }, []);

  // Use ref to keep stable handler reference across renders
  const handleNewEventRef = useRef(handleNewEvent);
  useEffect(() => {
    handleNewEventRef.current = handleNewEvent;
  }, [handleNewEvent]);

  // Create stable wrapper function ONCE using useRef + useCallback
  const stableHandlerRef = useRef(null);
  if (!stableHandlerRef.current) {
    stableHandlerRef.current = (data) => handleNewEventRef.current(data);
  }

  const handleEventUpdated = useCallback((data) => {
    setEvents(prev => prev.map(evt =>
      evt.id === data.event.id ? data.event : evt
    ));
  }, []);

  const handleEventDeleted = useCallback((data) => {
    setEvents(prev => prev.filter(evt => evt.id !== data.eventId));
  }, []);

  const handleViewerJoined = useCallback((data) => {
    setViewers(data.viewers);
  }, []);

  const handleViewerLeft = useCallback((data) => {
    setViewers(data.viewers);
  }, []);

  // Handle match joined + initial state
  useEffect(() => {
    if (!socket || !connected || !matchId) return;

    socket.off('match:joined', handleJoined);
    socket.on('match:joined', handleJoined);

    return () => {
      socket.off('match:joined', handleJoined);
    };
  }, [socket, connected, matchId, handleJoined]);

  // Handle score updates
  useEffect(() => {
    if (!socket || !connected || !matchId) return;

    socket.off('match:score:updated', handleScoreUpdate);
    socket.on('match:score:updated', handleScoreUpdate);

    return () => {
      socket.off('match:score:updated', handleScoreUpdate);
    };
  }, [socket, connected, matchId, handleScoreUpdate]);

  // Handle new events
  useEffect(() => {
    if (!socket || !connected || !matchId) return;

    const handler = stableHandlerRef.current;

    socket.off('match:event:new', handler);
    socket.on('match:event:new', handler);

    return () => {
      socket.off('match:event:new', handler);
    };
  }, [socket, connected, matchId]);

  // Handle event updates
  useEffect(() => {
    if (!socket || !connected || !matchId) return;

    socket.off('match:event:updated', handleEventUpdated);
    socket.on('match:event:updated', handleEventUpdated);

    return () => {
      socket.off('match:event:updated', handleEventUpdated);
    };
  }, [socket, connected, matchId, handleEventUpdated]);

  // Handle event deletions
  useEffect(() => {
    if (!socket || !connected || !matchId) return;

    socket.off('match:event:deleted', handleEventDeleted);
    socket.on('match:event:deleted', handleEventDeleted);

    return () => {
      socket.off('match:event:deleted', handleEventDeleted);
    };
  }, [socket, connected, matchId, handleEventDeleted]);

  // Handle viewer count updates
  useEffect(() => {
    if (!socket || !connected || !matchId) return;

    socket.off('match:viewer:joined', handleViewerJoined);
    socket.off('match:viewer:left', handleViewerLeft);
    socket.on('match:viewer:joined', handleViewerJoined);
    socket.on('match:viewer:left', handleViewerLeft);

    return () => {
      socket.off('match:viewer:joined', handleViewerJoined);
      socket.off('match:viewer:left', handleViewerLeft);
    };
  }, [socket, connected, matchId, handleViewerJoined, handleViewerLeft]);

  // Handle clock updates with stable handler
  const handleClockUpdate = useCallback((data) => {
    setClock({
      minute: data.minute,
      second: data.second,
      half: data.half,
      injuryTime: data.injuryTime,
      running: data.running
    });
  }, []);

  const handleLayerToggle = useCallback((data) => {
    if (data.layerId === 'formations') {
      setFormationsVisible(data.visible);
    }
  }, []);

  // Re-register clock listener on reconnect
  useEffect(() => {
    if (!socket || !connected || !matchId) return;

    socket.off('match:clock:update', handleClockUpdate);
    socket.on('match:clock:update', handleClockUpdate);

    return () => {
      socket.off('match:clock:update', handleClockUpdate);
    };
  }, [socket, connected, matchId, handleClockUpdate]);

  // Listen for overlay layer toggles
  useEffect(() => {
    if (!socket || !connected || !matchId) return;

    socket.off('overlay:layer:toggle', handleLayerToggle);
    socket.on('overlay:layer:toggle', handleLayerToggle);

    return () => {
      socket.off('overlay:layer:toggle', handleLayerToggle);
    };
  }, [socket, connected, matchId, handleLayerToggle]);

  return {
    connected,
    joined,
    events,
    score,
    viewers,
    matchState,
    match,
    clock,
    formationsVisible,
    socket
  };
};
